import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { applyPageMetadata, applyStructuredData } from '../utils/seo';

export default function BartenderJobsChicagoPage() {
  useEffect(() => {
    const cleanupMeta = applyPageMetadata({
      title: 'Bartender Jobs in Chicago, IL | BarsConnect',
      description: 'Find bartender jobs in Chicago, Illinois. Explore downtown, West Loop, and neighborhood bar shifts with direct bar-owner connections.',
      keywords: 'bartender jobs chicago il, chicago bartender shifts, bar jobs chicago, nightlife jobs chicago, event bartending chicago',
      canonicalUrl: 'https://barsconnect.com/bartender-jobs-chicago-il',
      ogTitle: 'Bartender Jobs in Chicago, IL | BarsConnect',
      ogDescription: 'Discover flexible bartender shifts and event gigs across Chicago.',
      twitterTitle: 'Bartender Jobs in Chicago, IL | BarsConnect',
      twitterDescription: 'Browse local bartending opportunities in Chicago and connect directly with bars.',
      robots: 'index,follow',
    });

    const cleanupSchema = applyStructuredData([
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://barsconnect.com/' },
          { '@type': 'ListItem', position: 2, name: 'Bartender Jobs', item: 'https://barsconnect.com/bartender-jobs' },
          { '@type': 'ListItem', position: 3, name: 'Chicago, IL', item: 'https://barsconnect.com/bartender-jobs-chicago-il' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: 'Bartender Jobs in Chicago, IL',
        description: 'Flexible bartender opportunities in Chicago, IL through BarsConnect.',
        employmentType: 'CONTRACTOR',
        hiringOrganization: {
          '@type': 'Organization',
          name: 'BarsConnect Partner Bars',
          sameAs: 'https://barsconnect.com',
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Chicago',
            addressRegion: 'IL',
            addressCountry: 'US',
          },
        },
      },
    ]);

    return () => {
      cleanupMeta();
      cleanupSchema();
    };
  }, []);

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="chicago-landing-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-3" data-testid="chicago-page-title">Bartender Jobs in Chicago, IL</h1>
        <p className="text-muted-foreground mb-8" data-testid="chicago-page-subtitle">
          Connect with bars hiring across Chicago and discover flexible shifts, event gigs, and nightlife roles.
        </p>
        <Card className="bg-white/5 border border-white/10" data-testid="chicago-cta-card">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground mb-4" data-testid="chicago-cta-copy">
              Chicago bars move fast on weekends and events. Build your profile now so owners can find you first.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="chicago-create-profile-btn"><Link to="/register/bartenders">Create Free Bartender Profile</Link></Button>
              <Button asChild variant="outline" data-testid="chicago-browse-bartenders-btn"><Link to="/bartenders">Browse Bartenders</Link></Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
